import { captureHandledException } from '@/lib/sentry';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ContentLibraryData, saveCategory, getContentLibrariesByModule } from '@/lib/firebase';
import { useAuth } from '@/contexts/use-auth';
import { logger } from '@/lib/logger';

interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onCategoryAdded: () => void;
}

export default function AddCategoryModal({ open, onClose, onCategoryAdded }: AddCategoryModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tag: '',
    module: '',
    contentLibrary: ''
  });
  const [contentLibraries, setContentLibraries] = useState<ContentLibraryData[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Load content libraries when module changes
  useEffect(() => {
    const loadContentLibraries = async () => {
      if (formData.module) {
        try {
          const libraries = await getContentLibrariesByModule(formData.module);
          logger.log(`Loaded ${libraries.length} libraries for module "${formData.module}":`, libraries);
          setContentLibraries(libraries);
        } catch (error) {
          logger.error('Failed to load content libraries:', error);
          setContentLibraries([]);
        }
      } else {
        setContentLibraries([]);
      }
    };

    loadContentLibraries();
  }, [formData.module]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({
        name: '',
        description: '',
        tag: '',
        module: '',
        contentLibrary: ''
      });
      setContentLibraries([]);
    }
  }, [open]);

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !formData.name.trim() || !formData.module || !formData.contentLibrary) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setLoading(true);
      
      await saveCategory({
        title: formData.name.trim(),
        description: formData.description.trim(),
        tag: formData.tag.trim() || formData.name.toLowerCase().replace(/\s+/g, ''),
        module: formData.module,
        contentLibrary: formData.contentLibrary,
      });
      
      onCategoryAdded();
      onClose();
      
      toast({
        title: "Category created",
        description: `"${formData.name}" has been added to your categories.`,
      });
    } catch (error: any) {
      captureHandledException(error, "category.create");
      toast({
        title: "Failed to create category",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Category</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleAddCategory} className="space-y-4">
          {/* Name Field */}
          <div>
            <Label htmlFor="categoryName" className="text-sm text-slate-600">Name *</Label>
            <Input
              id="categoryName"
              type="text"
              placeholder="Category name..."
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1"
            />
          </div>
          
          {/* Description Field */}
          <div>
            <Label htmlFor="categoryDescription" className="text-sm text-slate-600">Description</Label>
            <Textarea
              id="categoryDescription"
              placeholder="Category description..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1"
              rows={3}
            />
          </div>
          
          {/* Tag Field */}
          <div>
            <Label htmlFor="categoryTag" className="text-sm text-slate-600">Tag</Label>
            <Input
              id="categoryTag"
              type="text"
              placeholder="Auto-generated from name if empty"
              value={formData.tag}
              onChange={(e) => setFormData(prev => ({ ...prev, tag: e.target.value }))}
              className="mt-1"
            />
          </div>
          
          {/* Module Selection */}
          <div>
            <Label htmlFor="categoryModule" className="text-sm text-slate-600">Module *</Label>
            <Select 
              value={formData.module} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, module: value, contentLibrary: '' }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="mind">Mind</SelectItem>
                <SelectItem value="body">Body</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Content Library Selection */}
          <div>
            <Label htmlFor="categoryLibrary" className="text-sm text-slate-600">Content Library *</Label>
            <Select 
              value={formData.contentLibrary} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, contentLibrary: value }))}
              disabled={!formData.module}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={formData.module ? "Select content library" : "Select module first"} />
              </SelectTrigger>
              <SelectContent>
                {contentLibraries.map((library, index) => (
                  <SelectItem 
                    key={library.id || `library-${index}`} 
                    value={library.key || library.name || ''}
                  >
                    {library.name || library.title || 'Unnamed Library'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !formData.name.trim() || !formData.module || !formData.contentLibrary}
            >
              {loading ? 'Creating...' : 'Create Category'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}